import React from 'react';
import './M3Card.css';

/**
 * M3 Card — three variants: filled, outlined, elevated.
 *
 * @param {'filled'|'outlined'|'elevated'} [variant='filled']
 * @param {string} [media] — optional image/icon slot
 * @param {ReactNode} [action] — optional action slot (icon button)
 * @param {string} [className]
 * @param {boolean} [clickable] — adds ripple + hover state
 */
export default function M3Card({
  variant = 'filled',
  media,
  action,
  className = '',
  clickable = false,
  children,
  ...props
}) {
  return (
    <div
      className={`m3-card m3-card-${variant} ${clickable ? 'm3-card-clickable' : ''} ${className}`}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      {...props}
    >
      {media && <div className="m3-card-media">{media}</div>}
      <div className="m3-card-content">{children}</div>
      {action && <div className="m3-card-actions m3-card-actions-trailing">{action}</div>}
    </div>
  );
}

/**
 * M3 Card with title and subtitle — common pattern.
 */
export function M3CardWithHeader({
  variant,
  media,
  action,
  title,
  subtitle,
  children,
  clickable,
  className
}) {
  return (
    <M3Card
      variant={variant}
      media={media}
      action={action}
      clickable={clickable}
      className={className}
    >
      {title && <h3 className="m3-card-title">{title}</h3>}
      {subtitle && <p className="m3-card-subtitle">{subtitle}</p>}
      {children && <div className="m3-card-body">{children}</div>}
    </M3Card>
  );
}

/**
 * M3 Card Header — title + subtitle + action slot.
 */
export function M3CardHeader({ title, subtitle, action, children }) {
  return (
    <>
      {title && <h3 className="m3-card-title">{title}</h3>}
      {subtitle && <p className="m3-card-subtitle">{subtitle}</p>}
      {action && <div className="m3-card-actions m3-card-actions-trailing">{action}</div>}
      {children && <div className="m3-card-body">{children}</div>}
    </>
  );
}

/**
 * M3 Card Body — content wrapper.
 */
export function M3CardBody({ children }) {
  return <div className="m3-card-body">{children}</div>;
}

// Attach sub-components to default export
M3Card.Header = M3CardHeader;
M3Card.Body = M3CardBody;
M3Card.WithHeader = M3CardWithHeader;
